import React, { useState } from 'react';
import { Mail, Phone, ArrowRight, MessageCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';

export const ContactSection = ({ formData, setFormData, handleSubmit }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Preparar dados no formato esperado pelo Google Apps Script
      const dataToSend = {
        nome: formData.name,
        email: formData.email,
        empresa: formData.company || '',
        interesse: formData.type,
        mensagem: formData.message
      };

      // Enviar dados para Google Apps Script
      // Com no-cors, a requisição é enviada mas não podemos ler a resposta
      fetch('https://script.google.com/macros/s/AKfycby2o1SHwLM-FtzM7csWWcoNmg_x40DXG3qjhdFIPqv3YVbcamBy6APUi3LR-5DTLxSY/exec', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(dataToSend),
        mode: 'no-cors'
      }).catch(err => console.log('Fetch error (esperado com no-cors):', err));

      // Aguardar um pouco para a requisição ser enviada
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Mostrar mensagem de sucesso
      setShowThankYou(true);
      setFormData({ name: '', email: '', company: '', message: '', type: 'empresa' });
      
      // Esconder mensagem de agradecimento após 5 segundos
      setTimeout(() => {
        setShowThankYou(false);
      }, 5000);

    } catch (error) {
      console.error('Erro ao enviar formulário:', error);
      alert('Erro ao enviar formulário. Por favor, tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWhatsAppSubmit = (e) => {
    e.preventDefault();
    
    // Mapear o tipo de interesse para texto legível
    const interestMap = {
      'empresa': 'Empresa / Investidor ESG',
      'produtor': 'Produtor / Comunidade',
      'parceiro': 'Potencial Parceiro',
      'outro': 'Outro'
    };

    // Compor mensagem do WhatsApp
    const message = `Olá, meu nome é ${formData.name} e meu email de contato é ${formData.email}${formData.company ? ` sou da empresa ${formData.company}` : ''} e sou ${interestMap[formData.type] || formData.type}. ${formData.message}`;
    
    // Codificar URL
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/5561996355788?text=${encodedMessage}`;
    
    // Abrir WhatsApp
    window.open(whatsappUrl, '_blank');
  };

  return (
    <section id="contato" className="py-24 bg-ecam-petrol text-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-5">
        <img 
          src="images/svg/Semi-infinito%20Ecam%2001.svg" 
          alt=""
          className="absolute left-0 bottom-0 h-full"
        />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16 animate-on-scroll">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Quer fazer parte dessa transformação?
            </h2>
            <p className="text-xl opacity-90">
              Invista em créditos de carbono que regeneram florestas e fortalecem vidas.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="animate-on-scroll">
              {showThankYou ? (
                <Card className="bg-white text-gray-900">
                  <CardContent className="p-8 text-center">
                    <div className="mb-6">
                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                        </svg>
                      </div>
                      <h3 className="text-2xl font-bold text-ecam-forest mb-4">Mensagem Enviada!</h3>
                      <p className="text-gray-600 mb-6">
                        Obrigado pelo seu interesse. Nossa equipe entrará em contato em breve.
                      </p>
                      <Button 
                        onClick={() => setShowThankYou(false)}
                        className="bg-ecam-yellow hover:bg-ecam-yellow-dark text-gray-900 font-semibold"
                      >
                        Enviar outra mensagem
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card className="bg-white text-gray-900">
                  <CardContent className="p-8">
                    <h3 className="text-2xl font-bold text-ecam-forest mb-6">Envie uma mensagem</h3>
                    <form className="space-y-6">
                      <div>
                        <label className="block text-sm font-semibold mb-2 text-gray-700">Nome *</label>
                        <Input 
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="Seu nome completo"
                          required
                          className="border-gray-300 focus:border-ecam-blue focus:ring-ecam-blue"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-semibold mb-2 text-gray-700">Email *</label>
                        <Input 
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="seu@email.com"
                          required
                          className="border-gray-300 focus:border-ecam-blue focus:ring-ecam-blue"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-semibold mb-2 text-gray-700">Empresa / Organização</label>
                        <Input 
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          placeholder="Nome da sua empresa"
                          className="border-gray-300 focus:border-ecam-blue focus:ring-ecam-blue"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold mb-2 text-gray-700">Interesse *</label>
                        <select
                          name="type"
                          value={formData.type}
                          onChange={handleChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:border-ecam-blue focus:ring-ecam-blue focus:outline-none"
                          required
                        >
                          <option value="empresa">Empresa / Investidor ESG</option>
                          <option value="produtor">Produtor / Comunidade</option>
                          <option value="parceiro">Potencial Parceiro</option>
                          <option value="outro">Outro</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-semibold mb-2 text-gray-700">Mensagem *</label>
                        <Textarea 
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          placeholder="Como podemos ajudar?"
                          rows={4}
                          required
                          className="border-gray-300 focus:border-ecam-blue focus:ring-ecam-blue resize-none"
                        />
                      </div>
                      
                      {/* Botões de Envio */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Button 
                          onClick={handleEmailSubmit}
                          disabled={isSubmitting}
                          className="w-full bg-ecam-blue hover:bg-ecam-blue/90 text-white font-semibold py-6 flex items-center justify-center text-sm sm:text-base px-4"
                        >
                          {isSubmitting ? (
                            <>
                              <Loader2 className="mr-1.5 sm:mr-2 h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0 animate-spin" />
                              <span className="truncate">Enviando...</span>
                            </>
                          ) : (
                            <>
                              <Mail className="mr-1.5 sm:mr-2 h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
                              <span className="truncate">Enviar Email</span>
                            </>
                          )}
                        </Button>
                        
                        <Button 
                          onClick={handleWhatsAppSubmit}
                          type="button"
                          className="w-full bg-ecam-yellow hover:bg-ecam-yellow-dark text-gray-900 font-semibold py-6 flex items-center justify-center text-sm sm:text-base px-4"
                        >
                          <MessageCircle className="mr-1.5 sm:mr-2 h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
                          <span className="truncate">Enviar WhatsApp</span>
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Contact Info */}
            <div className="space-y-8 animate-on-scroll">
              <div>
                <h3 className="text-2xl font-bold mb-6">Fale diretamente conosco</h3>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-ecam-cordel/20 p-3 rounded-lg">
                      <Mail className="h-6 w-6 text-ecam-cordel" />
                    </div>
                    <div>
                      <p className="font-semibold mb-1">Email</p>
                      <a 
                        href="mailto:contato@ecam.org.br" 
                        className="text-ecam-cordel hover:text-ecam-yellow transition-colors"
                      >
                        contato@ecam.org.br
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="bg-ecam-cordel/20 p-3 rounded-lg">
                      <Phone className="h-6 w-6 text-ecam-cordel" />
                    </div>
                    <div>
                      <p className="font-semibold mb-1">WhatsApp</p>
                      <a 
                        href="https://wa.me/5561996355788" 
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-ecam-cordel hover:text-ecam-yellow transition-colors"
                      >
                        +55 (61) 99635-5788
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white/10 backdrop-blur p-6 rounded-xl border-2 border-ecam-cordel/30">
                <h4 className="font-bold text-xl mb-4">Para Empresas e Investidores ESG</h4>
                <p className="opacity-90 leading-relaxed">
                  Descubra como sua empresa pode compensar emissões de carbono enquanto gera impacto social positivo em comunidades brasileiras.
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur p-6 rounded-xl border-2 border-ecam-cordel/30">
                <h4 className="font-bold text-xl mb-4">Para Produtores e Comunidades</h4>
                <p className="opacity-90 leading-relaxed">
                  Saiba como participar do projeto e fortalecer práticas regenerativas em seu território.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer Component
export const Footer = () => {
  return (
    <footer className="bg-ecam-forest-dark py-12 border-t border-white/10">
      <div className="container mx-auto px-6">
        <div className="grid md:grid-cols-4 gap-8">
          <div>
            <img 
              src="/images/svg/Logo Ecam Horizontal.svg"
              alt="Ecam Logo" 
              className="h-10 mb-4 brightness-0 invert"
            />
            <p className="text-gray-400 text-sm">
              Cultivando o futuro através da regeneração e do impacto social.
            </p>
          </div>
          
          <div>
            <h4 className="text-white font-bold mb-4">Navegação</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li><a href="#sobre" className="hover:text-ecam-aqua transition-colors">Sobre o Projeto</a></li>
              <li><a href="#como-funciona" className="hover:text-ecam-aqua transition-colors">Como Funciona</a></li>
              <li><a href="#territorios" className="hover:text-ecam-aqua transition-colors">Territórios</a></li>
              <li><a href="#impacto" className="hover:text-ecam-aqua transition-colors">Impacto</a></li>
            </ul>
          </div>
          
          <div>
            <h4 className="text-white font-bold mb-4">Realização</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li>
                <a href="https://reseed.farm" target="_blank" rel="noopener noreferrer" className="hover:text-ecam-aqua transition-colors">
                  ReSeed
                </a>
              </li>
              <li>
                <a href="https://ecam.org.br" target="_blank" rel="noopener noreferrer" className="hover:text-ecam-aqua transition-colors">
                  Ecam
                </a>
              </li>
            </ul>
          </div>
          
          <div>
            <h4 className="text-white font-bold mb-4">Contato</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li>
                <a href="mailto:contato@ecam.org.br" className="hover:text-ecam-aqua transition-colors">
                  contato@ecam.org.br
                </a>
              </li>
              <li>
                <a href="https://wa.me/5561996355788" target="_blank" rel="noopener noreferrer" className="hover:text-ecam-aqua transition-colors">
                  WhatsApp
                </a>
              </li>
              <li>
                <a href="https://ecam.org.br" target="_blank" rel="noopener noreferrer" className="hover:text-ecam-aqua transition-colors">
                  ecam.org.br
                </a>
              </li>
            </ul>
          </div>
        </div>
        
        <div className="border-t border-white/10 mt-8 pt-8">
          {/* Nota explicativa do asterisco */}
          <div className="text-center text-gray-500 text-xs mb-6 max-w-3xl mx-auto">
            <p>
              * Aproximadamente 3 milhões em toneladas de CO₂ de emissão evitadas durante os 20 anos de projeto. 
              Números totais podem variar para mais ou para menos.
            </p>
          </div>
          
          {/* Copyright */}
            <div className="text-center text-gray-400 text-sm space-y-1">
              <p>&copy; 2025 Ecam - Carbono Social. Todos os direitos reservados.</p>
              <p className="text-xs text-gray-500">
                made with love by ♡ {" "}
                <a
                  href="https://instagram.com/raphaelrabelo"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-emerald-600 transition-colors duration-200 underline decoration-dotted"
                >
                  Raphael Rabelo
                </a>
              </p>
            </div>
      </div>
      </div>
    </footer>
  );
};